import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Send, AlertTriangle } from "lucide-react";
import { Button } from "../../components/ui/button";
import { PageHeader, StatusBadge } from "../../components/Common";
import { OrderDialog } from "../../components/OrderDialog";
import { api, currentMonth, dateLabel, monthLabel, todayStr } from "../../lib/api";

export const DAYS = ["Sen", "Sel", "Rab", "Kam", "Jum", "Sab", "Min"];
export const CHIP = { order: "bg-sky-100 text-sky-800", pending: "bg-amber-100 text-amber-800", paid: "bg-emerald-100 text-emerald-800" };

export const shiftMonth = (m, d) => {
  const [y, mo] = m.split("-").map(Number);
  const dt = new Date(y, mo - 1 + d, 1);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`;
};

export function buildCells(month) {
  const [y, mo] = month.split("-").map(Number);
  const lead = (new Date(y, mo - 1, 1).getDay() + 6) % 7;
  const count = new Date(y, mo, 0).getDate();
  return [...Array(lead).fill(null), ...Array.from({ length: count }, (_, i) => `${month}-${String(i + 1).padStart(2, "0")}`)];
}

const clashDrivers = (list, vendors = new Set()) => {
  const seen = {};
  list.filter((t) => !vendors.has(t.driver_id)).forEach((t) => { seen[t.driver_id] = (seen[t.driver_id] || 0) + 1; });
  return new Set(Object.keys(seen).filter((k) => seen[k] > 1));
};

function DayCell({ date, trips, offs = [], vendors, selected, onSelect }) {
  if (!date) return <div className="min-h-[56px] sm:min-h-[110px]" />;
  const clashes = clashDrivers(trips, vendors);
  const isToday = date === todayStr();
  return (
    <button
      onClick={() => onSelect(date)}
      className={`min-h-[56px] sm:min-h-[110px] rounded-xl border p-1.5 sm:p-2 text-left transition-colors ${selected ? "border-emerald-500 ring-2 ring-emerald-200 bg-emerald-50/40" : "border-slate-200 bg-white hover:border-slate-300"} ${clashes.size ? "!border-red-400" : ""}`}
      data-testid={`cal-day-${date}`}
    >
      <div className="flex items-center justify-between">
        <span className={`text-xs font-bold ${isToday ? "rounded-full bg-slate-900 text-white px-1.5" : "text-slate-700"}`}>{Number(date.slice(8))}</span>
        {clashes.size > 0 && <AlertTriangle className="h-3.5 w-3.5 text-red-500" data-testid={`cal-clash-${date}`} />}
      </div>
      <div className="hidden sm:block mt-1 space-y-1">
        {trips.slice(0, 3).map((t) => (
          <p key={t.id} className={`truncate rounded px-1.5 py-0.5 text-[10px] font-semibold ${clashes.has(t.driver_id) ? "bg-red-100 text-red-700" : CHIP[t.status]}`}>{t.driver_name}</p>
        ))}
        {trips.length > 3 && <p className="text-[10px] text-slate-500">+{trips.length - 3} lagi</p>}
        {offs.map((o) => <p key={o.id} className="truncate rounded px-1.5 py-0.5 text-[10px] font-semibold bg-slate-200 text-slate-600" data-testid={`cal-off-${o.id}`}>Libur · {o.driver_name}</p>)}
      </div>
      {offs.length > 0 && <p className="sm:hidden mt-0.5 text-[10px] font-semibold text-slate-400">{offs.length} libur</p>}
      {trips.length > 0 && <p className="sm:hidden mt-1 text-[10px] font-bold text-emerald-700" data-testid={`cal-count-${date}`}>{trips.length} trip</p>}
    </button>
  );
}

function DayPanel({ date, trips, offs = [], vendors, drivers, onCreate }) {
  const clashes = clashDrivers(trips, vendors);
  const busy = new Set([...trips.filter((t) => !vendors.has(t.driver_id)).map((t) => t.driver_id), ...offs.map((o) => o.driver_id)]);
  const free = drivers.filter((d) => d.active && !busy.has(d.id));
  return (
    <aside className="rounded-2xl border border-slate-200 bg-white p-5 space-y-5 lg:sticky lg:top-6" data-testid="day-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-emerald-600">Jadwal</p>
          <h2 className="font-heading text-xl font-bold text-slate-900" data-testid="day-panel-date">{dateLabel(date)}</h2>
        </div>
        <Button size="sm" onClick={() => onCreate("")} className="bg-slate-900 hover:bg-slate-800" data-testid="day-create-order-btn"><Send className="h-4 w-4 mr-1" />Order</Button>
      </div>
      <div className="space-y-2">
        <p className="text-xs font-semibold text-slate-500">Trip hari ini ({trips.length})</p>
        {trips.length === 0 && <p className="text-sm text-slate-400">Belum ada trip.</p>}
        {trips.map((t) => (
          <div key={t.id} className={`rounded-xl border p-3 ${clashes.has(t.driver_id) ? "border-red-300 bg-red-50" : "border-slate-100 bg-slate-50"}`} data-testid={`day-trip-${t.id}`}>
            <div className="flex items-center justify-between gap-2">
              <p className="font-semibold text-sm text-slate-900">{t.driver_name}</p>
              <StatusBadge status={t.status} />
            </div>
            <p className="text-xs text-slate-600 mt-0.5">{t.tour_name}</p>
            {clashes.has(t.driver_id) && <p className="text-[11px] font-semibold text-red-600 mt-1">Bentrok: driver punya lebih dari 1 trip</p>}
          </div>
        ))}
      </div>
      <div className="space-y-2">
        <p className="text-xs font-semibold text-slate-500">Driver libur / off ({offs.length})</p>
        <div className="flex flex-wrap gap-2">
          {offs.map((o) => <span key={o.id} className="rounded-full bg-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600" data-testid={`off-driver-${o.driver_id}`}>{o.driver_name}{o.note && ` · ${o.note}`}</span>)}
          {offs.length === 0 && <p className="text-sm text-slate-400">Tidak ada.</p>}
        </div>
      </div>
      <div className="space-y-2">
        <p className="text-xs font-semibold text-slate-500">Driver tersedia ({free.length})</p>
        <div className="flex flex-wrap gap-2">
          {free.map((d) => (
            <button key={d.id} onClick={() => onCreate(d.id)} className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-800 hover:bg-emerald-100 min-h-[44px] sm:min-h-[32px]" data-testid={`free-driver-${d.id}`}>
              + {d.name}{d.is_vendor ? " (Vendor)" : ""}
            </button>
          ))}
          {free.length === 0 && <p className="text-sm text-slate-400">Semua driver sudah terjadwal.</p>}
        </div>
      </div>
    </aside>
  );
}

export default function AdminSchedule() {
  const [month, setMonth] = useState(currentMonth());
  const [trips, setTrips] = useState([]);
  const [offs, setOffs] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [selected, setSelected] = useState(todayStr());
  const [orderOpen, setOrderOpen] = useState(false);
  const [orderDriver, setOrderDriver] = useState("");

  const load = useCallback(() => Promise.all([api.get("/trips", { params: { month } }), api.get("/dayoffs", { params: { month } })])
    .then(([t, o]) => { setTrips(t.data); setOffs(o.data); }), [month]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/users").then((r) => setDrivers(r.data)); }, []);

  const byDay = useMemo(() => trips.reduce((m, t) => ({ ...m, [t.trip_date]: [...(m[t.trip_date] || []), t] }), {}), [trips]);
  const offsByDay = useMemo(() => offs.reduce((m, o) => ({ ...m, [o.date]: [...(m[o.date] || []), o] }), {}), [offs]);
  const vendors = useMemo(() => new Set(drivers.filter((d) => d.is_vendor).map((d) => d.id)), [drivers]);
  const changeMonth = (d) => { const m = shiftMonth(month, d); setMonth(m); setSelected(`${m}-01`); };
  const create = (driverId) => { setOrderDriver(driverId); setOrderOpen(true); };

  return (
    <div>
      <PageHeader eyebrow="Jadwal Order Harian" title={monthLabel(month)}>
        <div className="flex gap-1">
          <Button variant="outline" size="icon" onClick={() => changeMonth(-1)} data-testid="cal-prev-btn"><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" onClick={() => { setMonth(currentMonth()); setSelected(todayStr()); }} data-testid="cal-today-btn">Hari ini</Button>
          <Button variant="outline" size="icon" onClick={() => changeMonth(1)} data-testid="cal-next-btn"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </PageHeader>
      <div className="flex flex-wrap gap-3 mb-4 text-[11px] font-semibold">
        <span className="rounded px-2 py-1 bg-sky-100 text-sky-800">Order</span>
        <span className="rounded px-2 py-1 bg-amber-100 text-amber-800">Belum Dibayar</span>
        <span className="rounded px-2 py-1 bg-emerald-100 text-emerald-800">Lunas</span>
        <span className="rounded px-2 py-1 bg-red-100 text-red-700">Bentrok</span>
        <span className="rounded px-2 py-1 bg-slate-200 text-slate-600">Libur</span>
      </div>
      <div className="grid lg:grid-cols-[1fr_340px] gap-6 items-start">
        <div data-testid="calendar">
          <div className="grid grid-cols-7 gap-1 sm:gap-2 mb-1">{DAYS.map((d) => <p key={d} className="text-center text-[11px] font-semibold uppercase text-slate-400">{d}</p>)}</div>
          <div className="grid grid-cols-7 gap-1 sm:gap-2">
            {buildCells(month).map((date, i) => <DayCell key={date || `e${i}`} date={date} trips={byDay[date] || []} offs={offsByDay[date] || []} vendors={vendors} selected={date === selected} onSelect={setSelected} />)}
          </div>
        </div>
        <DayPanel date={selected} trips={byDay[selected] || []} offs={offsByDay[selected] || []} vendors={vendors} drivers={drivers} onCreate={create} />
      </div>
      <OrderDialog open={orderOpen} onOpenChange={setOrderOpen} order={null} vendor={vendors.has(orderDriver)} defaultDriverId={orderDriver} defaultDate={selected} onSaved={load} />
    </div>
  );
}
